import React, { useState } from 'react';
import { useUser } from '../context/UserContext';
import {
  Box, Container, Typography, Grid, Card, Chip, Button,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import FavoriteIcon from '@mui/icons-material/Favorite';
import WorkIcon from '@mui/icons-material/Work';
import CakeIcon from '@mui/icons-material/Cake';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import StarIcon from '@mui/icons-material/Star';
import { localYmd, PRODUCTS } from '../services/paymentService';
import ReadingWizardModal from '../components/ReadingWizardModal';

// "Coming soon" tag (Claudia 2026-10-14: WhatsApp, calls, "What do you need?" and Meet Claudia are not live yet)
const ComingSoonChip = ({ large = false, sx = {} }) => (
  <Chip
    label="Coming soon"
    size="small"
    sx={{
      height: large ? 24 : 18,
      fontSize: large ? '0.75rem' : '0.6rem',
      fontWeight: 700,
      letterSpacing: 0.5,
      bgcolor: 'rgba(201,168,124,0.15)',
      color: '#c9a87c',
      border: '1px solid rgba(201,168,124,0.3)',
      ...sx,
    }}
  />
);

// "Choose Your Cosmic Journey" says which kind of reading each card is (Claudia 2026-10-14): a PERSONAL
// reading is calculated from the buyer's own birth chart; a SIGN forecast is the same for everyone with
// that rising sign, whatever their personal planets.
const KINDS = {
  personal: { label: 'Personal reading', note: 'Calculated from your own birth chart', color: '#e8a98f' },
  sign: { label: 'For every sign', note: 'Written for your rising sign', color: '#7dd3fc' },
};
const KindChip = ({ kind }) => (
  <Chip
    label={KINDS[kind].label}
    size="small"
    sx={{
      height: 20,
      fontSize: '0.65rem',
      fontWeight: 700,
      letterSpacing: 0.3,
      color: KINDS[kind].color,
      bgcolor: `${KINDS[kind].color}1f`,
      border: `1px solid ${KINDS[kind].color}55`,
    }}
  />
);

/** 1500 → '15', 2999 → '29.99' */
const euros = (cents) => (cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2));

// Subtle animated cosmic particles (CSS only)
const CosmicParticles = () => (
  <Box sx={{
    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
    pointerEvents: 'none', zIndex: 1, overflow: 'hidden',
    '& .star-particle': {
      position: 'absolute',
      width: '2px', height: '2px',
      background: 'rgba(201, 168, 124, 0.5)',
      borderRadius: '50%',
      animation: 'twinkle 3s ease-in-out infinite',
    },
    '@keyframes twinkle': {
      '0%, 100%': { opacity: 0.2, transform: 'scale(1)' },
      '50%': { opacity: 1, transform: 'scale(1.5)' },
    }
  }}>
    {[...Array(30)].map((_, i) => (
      <span key={i} className="star-particle" style={{
        top: `${Math.random() * 100}%`,
        left: `${Math.random() * 100}%`,
        animationDelay: `${Math.random() * 5}s`,
        animationDuration: `${2 + Math.random() * 3}s`,
      }} />
    ))}
  </Box>
);

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const HomePage = () => {
  const navigate = useNavigate();
  const { isLoggedIn } = useUser();
  const [wizardOpen, setWizardOpen] = useState(false);

  const intentCards = [
    {
      icon: <FavoriteIcon sx={{ fontSize: 32, color: '#ef4444' }} />,
      title: 'Love & Relationships',
      desc: 'Are you cosmically compatible?',
      badge: 'Coming Soon',
      path: '/compatibility',
      gradient: 'linear-gradient(135deg, rgba(239,68,68,0.1), rgba(239,68,68,0.02))',
    },
    {
      icon: <WorkIcon sx={{ fontSize: 32, color: '#f59e0b' }} />,
      title: 'Career & Money',
      desc: 'Timing is everything',
      path: '/explore?intent=career',
      gradient: 'linear-gradient(135deg, rgba(245,158,11,0.1), rgba(245,158,11,0.02))',
    },
    {
      icon: <CakeIcon sx={{ fontSize: 32, color: '#7b68ee' }} />,
      title: 'Birthday Gift',
      desc: 'The perfect cosmic gift',
      path: '/gift-shop',
      gradient: 'linear-gradient(135deg, rgba(123,104,238,0.1), rgba(123,104,238,0.02))',
    },
    {
      icon: <PhoneInTalkIcon sx={{ fontSize: 32, color: '#4ade80' }} />,
      title: 'Talk to the Stars',
      desc: '1:1 sessions with Claudia coming soon',
      path: '/claudia',
      gradient: 'linear-gradient(135deg, rgba(74,222,128,0.1), rgba(74,222,128,0.02))',
    },
  ];

  // The personal transit calendar (transits to the natal chart): its weekly view is free once you have an account,
  // so the card starts the free sign-up, or opens the calendar for someone who already has one.
  const hasCalendarAccount = (() => {
    try { return !!localStorage.getItem('transit-calendar-token'); } catch { return false; }
  })();

  // Order (Claudia 2026-10-14/15): the Venus reading, the Year Ahead maps, the 2027 extended videos, the personal
  // calendar, then the coming-soon ones
  const products = [
    {
      name: 'Your Venus Star Point 2026–2028',
      kind: 'personal',
      price: '49',
      interval: '',
      desc: 'Your personal reading: what you plant now blooms',
      color: '#e8a98f',
      path: '/venus',
    },
    {
      name: 'Year Ahead 2027',
      kind: 'sign',
      price: '29.99',
      interval: '',
      desc: 'Your 2027 house & transit maps',
      color: '#4ade80',
      path: '/year-ahead-2027',
      // until 31 Oct 2026: free with a 2027 Extended Forecast video order
      offer: localYmd() <= '2026-10-31'
        ? { text: 'Free until 31 Oct with your 2027 Extended Forecast video', link: 'See the 2027 forecast', to: '/pricing/shop' }
        : null,
    },
    {
      name: '2027 Extended Forecast Videos',
      kind: 'sign',
      // today's price of one sign (the month's discount applies: paymentService PRODUCTS)
      price: euros(PRODUCTS.SINGLE_RISING.amount_cents),
      interval: 'a sign',
      desc: 'House by house, month by month, for your rising sign',
      color: '#7b9cff',
      path: '/pricing/shop',
    },
    {
      name: 'Your Personal Cosmic Calendar',
      kind: 'personal',
      Icon: CalendarMonthIcon,
      free: true,
      interval: 'weekly view',
      desc: "Transits to your natal chart: the exact days today's planets touch your own birth chart, week by week",
      color: '#5eead4',
      path: hasCalendarAccount ? '/transit-calendar/profiles' : '/transit-calendar/register',
      offer: { text: 'Upgrade any time to see the whole month or the whole year', link: 'See the calendar plans', to: '/pricing?section=nt-calendar' },
    },
    {
      name: 'Stars Made Simple',
      kind: 'personal',
      badge: 'Coming soon',
      comingSoon: true,
      desc: 'Daily guidance in plain English',
      color: '#c9a87c',
    },
    {
      name: 'Compatibility Check',
      kind: 'personal',
      free: true,
      interval: 'two charts',
      desc: 'Are you meant to be? Two birth charts compared planet by planet',
      color: '#ef4444',
      path: '/compatibility',
    },
    {
      name: 'Birthday Star Portrait',
      kind: 'personal',
      badge: 'Coming soon',
      comingSoon: true,
      desc: 'The perfect cosmic gift',
      color: '#7b68ee',
    },
  ];

  return (
    <Box sx={{ bgcolor: '#0a0a0f', color: '#ede8e0' }}>

      {/* ============ SECTION 1: HERO ============ */}
      <Box
        sx={{
          position: 'relative',
          // Claudia 2026-10-15: shorter, so Today's Cosmic Weather and everything below it sit higher
          minHeight: { xs: '72vh', md: '62vh' },
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          background: 'radial-gradient(ellipse at 50% 0%, #1a1a2e 0%, #0a0a0f 60%)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 20%, rgba(201,168,124,0.06) 0%, transparent 50%), radial-gradient(circle at 70% 60%, rgba(123,104,238,0.04) 0%, transparent 50%)',
            zIndex: 1,
          },
        }}
      >
        <CosmicParticles />
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 2, textAlign: 'center', py: 6 }}>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
                  mb: 2,
                  lineHeight: 1.2,
                  background: 'linear-gradient(135deg, #ede8e0 0%, #c9a87c 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                What do the stars say about YOUR life right now?
              </Typography>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <Typography
                variant="body1"
                sx={{ color: '#8a8a9a', mb: 4, fontSize: { xs: '0.95rem', md: '1.1rem' } }}
              >
                Real astrology calculations. Plain English guidance. From Ireland.
              </Typography>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <Button
                variant="contained"
                size="large"
                onClick={() => setWizardOpen(true)}
                endIcon={<AutoAwesomeIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #c9a87c 0%, #a88a5e 100%)',
                  color: '#0a0a0f',
                  fontWeight: 700,
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  px: { xs: 4, md: 5 },
                  py: 1.5,
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(201,168,124,0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #dbc4a0 0%, #c9a87c 100%)',
                    boxShadow: '0 6px 28px rgba(201,168,124,0.4)',
                  },
                }}
              >
                Get My Free Reading
              </Button>
            </motion.div>

            {/* Channel CTAs - WhatsApp & Phone: coming soon (Claudia 2026-10-14) */}
            <motion.div variants={fadeInUp}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<WhatsAppIcon />}
                  disabled
                  sx={{
                    fontWeight: 600,
                    px: 3,
                    '&.Mui-disabled': { borderColor: 'rgba(74,222,128,0.35)', color: 'rgba(74,222,128,0.6)' },
                  }}
                >
                  Chat on WhatsApp
                  <ComingSoonChip sx={{ ml: 1 }} />
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PhoneInTalkIcon />}
                  disabled
                  sx={{
                    fontWeight: 600,
                    px: 3,
                    '&.Mui-disabled': { borderColor: 'rgba(201,168,124,0.3)', color: 'rgba(201,168,124,0.6)' },
                  }}
                >
                  Call Claudia
                  <ComingSoonChip sx={{ ml: 1 }} />
                </Button>
              </Box>
            </motion.div>

            {!isLoggedIn && (
              <motion.div variants={fadeInUp}>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 3,
                    color: '#8a8a9a',
                    cursor: 'pointer',
                    '&:hover': { color: '#c9a87c' },
                  }}
                  onClick={() => navigate('/my-stars')}
                >
                  Already a member? Log in
                </Typography>
              </motion.div>
            )}
          </motion.div>
        </Container>
      </Box>

      {/* Reading Wizard Modal */}
      <ReadingWizardModal
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSeePlans={() => navigate('/pricing?section=calendar')}
      />

      {/* ============ SECTION 2: TODAY'S COSMIC WEATHER ============ */}
      {/* Claudia 2026-10-14: no aspect list here any more; the card opens the Explore page (today's transits) */}
      <Box sx={{ pt: { xs: 1, md: 2 }, pb: { xs: 6, md: 8 }, bgcolor: '#0a0a0f' }}>
        <Container maxWidth="md">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp}>
              <Card
                onClick={() => navigate('/explore')}
                sx={{
                  cursor: 'pointer',
                  p: { xs: 3, md: 4 },
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #1a1a2e 0%, #141420 100%)',
                  border: '1px solid rgba(201,168,124,0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    border: '1px solid rgba(201,168,124,0.35)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                  },
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1.5, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                  Today's Cosmic Weather
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
                  <CalendarMonthIcon sx={{ color: '#c9a87c' }} />
                  <Typography sx={{ color: '#c9a87c', fontWeight: 600 }}>
                    {new Date().toLocaleDateString('en-IE', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </Typography>
                </Box>
                <Button
                  endIcon={<ArrowForwardIcon />}
                  sx={{ color: '#c9a87c', fontWeight: 600, '&:hover': { background: 'transparent', color: '#dbc4a0' } }}
                >
                  Explore today's sky
                </Button>
              </Card>
            </motion.div>
          </motion.div>
        </Container>
      </Box>

      {/* ============ SECTION 3: PRODUCT SHOWCASE ============ */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#141420' }}>
        <Container maxWidth="lg">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp}>
              <Typography
                variant="h4"
                align="center"
                sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.5rem', md: '2rem' } }}
              >
                Choose Your Cosmic Journey
              </Typography>
              <Typography variant="body1" align="center" sx={{ color: '#8a8a9a', mb: 2 }}>
                From daily guidance to full year forecasts
              </Typography>
              {/* the two kinds of reading */}
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 1.5, sm: 3 }, flexWrap: 'wrap', mb: 4 }}>
                {Object.keys(KINDS).map((k) => (
                  <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <KindChip kind={k} />
                    <Typography variant="body2" sx={{ color: '#8a8a9a', fontSize: '0.8rem' }}>
                      {KINDS[k].note}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </motion.div>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                overflowX: 'auto',
                pb: 2,
                px: 1,
                scrollSnapType: 'x mandatory',
                '&::-webkit-scrollbar': { height: 4 },
                '&::-webkit-scrollbar-track': { background: 'rgba(255,255,255,0.05)' },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(201,168,124,0.3)', borderRadius: 2 },
              }}
            >
              {products.map((product) => (
                <motion.div key={product.name} variants={fadeInUp} style={{ flexShrink: 0 }}>
                  <Card
                    onClick={product.comingSoon ? undefined : () => navigate(product.path || '/pricing')}
                    sx={{
                      width: { xs: 220, md: 260 },
                      height: '100%',
                      scrollSnapAlign: 'start',
                      cursor: product.comingSoon ? 'default' : 'pointer',
                      p: 3,
                      background: '#1a1a2e',
                      border: '1px solid rgba(201,168,124,0.08)',
                      transition: 'all 0.3s ease',
                      '&:hover': product.comingSoon ? {} : {
                        border: `1px solid ${product.color}33`,
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 40px ${product.color}15`,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 2 }}>
                      <Box
                        sx={{
                          width: 48, height: 48, borderRadius: 2, flexShrink: 0,
                          background: `${product.color}18`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        {React.createElement(product.Icon || StarIcon, { sx: { color: product.color, fontSize: 24 } })}
                      </Box>
                      <KindChip kind={product.kind} />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {product.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#8a8a9a', mb: 2, fontSize: '0.8rem' }}>
                      {product.desc}
                    </Typography>
                    {product.free ? (
                      <Typography variant="h5" sx={{ fontWeight: 700, color: product.color }}>
                        Free
                        <Typography component="span" variant="body2" sx={{ color: '#8a8a9a', ml: 0.5 }}>
                          {product.interval}
                        </Typography>
                      </Typography>
                    ) : product.price ? (
                      <Typography variant="h5" sx={{ fontWeight: 700, color: product.color }}>
                        EUR {product.price}
                        <Typography component="span" variant="body2" sx={{ color: '#8a8a9a', ml: 0.5 }}>
                          {product.interval}
                        </Typography>
                      </Typography>
                    ) : (
                      <Typography variant="h6" sx={{ fontWeight: 700, color: product.color }}>
                        {product.badge}
                      </Typography>
                    )}
                    {product.offer && (
                      <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(201,168,124,0.12)' }}>
                        <Typography variant="body2" sx={{ color: '#c9a87c', fontSize: '0.78rem', lineHeight: 1.45 }}>
                          {product.offer.text}
                        </Typography>
                        <Button
                          size="small"
                          onClick={(e) => { e.stopPropagation(); navigate(product.offer.to); }}
                          sx={{ color: '#c9a87c', p: 0, mt: 0.5, minWidth: 0, fontSize: '0.78rem', textTransform: 'none', textDecoration: 'underline' }}
                        >
                          {product.offer.link}
                        </Button>
                      </Box>
                    )}
                  </Card>
                </motion.div>
              ))}
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* ============ SECTION 4: INTENT SELECTOR ============ */}
      {/* Claudia 2026-10-14: coming soon (no links yet), below the products */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#0a0a0f' }}>
        <Container maxWidth="md">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp}>
              <Typography
                variant="h4"
                align="center"
                sx={{ fontWeight: 700, mb: 1.5, fontSize: { xs: '1.5rem', md: '2rem' } }}
              >
                What do you need?
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                <ComingSoonChip large />
              </Box>
            </motion.div>

            <Grid container spacing={2}>
              {intentCards.map((card) => (
                <Grid item xs={6} key={card.title}>
                  <motion.div variants={fadeInUp}>
                    <Card
                      sx={{
                        cursor: 'default',
                        p: { xs: 2, md: 3 },
                        textAlign: 'center',
                        background: card.gradient,
                        border: '1px solid rgba(201,168,124,0.08)',
                        opacity: 0.6,
                      }}
                    >
                      <Box sx={{ mb: 1.5 }}>
                        {card.icon}
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', md: '1rem' } }}>
                        {card.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#8a8a9a', fontSize: { xs: '0.75rem', md: '0.85rem' }, mt: 0.5 }}>
                        {card.desc}
                      </Typography>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* ============ SECTION 5: MEET CLAUDIA ============ */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#141420' }}>
        <Container maxWidth="md">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={staggerContainer}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={5}>
                <motion.div variants={fadeInUp}>
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: '3/4',
                      maxWidth: 280,
                      mx: 'auto',
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, #1a1a2e, #2a2a4e)',
                      border: '1px solid rgba(201,168,124,0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    <Typography variant="body2" sx={{ color: '#8a8a9a' }}>
                      Photo coming soon
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={7}>
                <motion.div variants={fadeInUp}>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '1.5rem', md: '2rem' } }}
                  >
                    Meet Claudia, Your Astrologer
                  </Typography>
                  <ComingSoonChip large sx={{ mb: 2 }} />
                  <Typography variant="body1" sx={{ color: '#ede8e0', mb: 3, lineHeight: 1.7 }}>
                    Professional astrologer translating cosmic patterns into life guidance
                    you can actually use. Based in Ireland, using real astronomical calculations,
                    not cookie-cutter templates.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      startIcon={<PlayCircleIcon />}
                      disabled
                      sx={{
                        background: 'linear-gradient(135deg, #c9a87c, #a88a5e)',
                        color: '#0a0a0f',
                        fontWeight: 600,
                        '&:hover': { background: 'linear-gradient(135deg, #dbc4a0, #c9a87c)' },
                        '&.Mui-disabled': { background: 'rgba(201,168,124,0.18)', color: 'rgba(237,232,224,0.45)' },
                      }}
                    >
                      Watch This Week's Video
                    </Button>
                    <Button
                      variant="outlined"
                      disabled
                      sx={{
                        borderColor: 'rgba(201,168,124,0.3)',
                        color: '#c9a87c',
                        '&:hover': { borderColor: '#c9a87c', background: 'rgba(201,168,124,0.05)' },
                        '&.Mui-disabled': { borderColor: 'rgba(201,168,124,0.18)', color: 'rgba(201,168,124,0.45)' },
                      }}
                    >
                      1:1 Sessions Coming Soon
                    </Button>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* ============ SECTION 6: TRUST SIGNALS ============ */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#0a0a0f' }}>
        <Container maxWidth="md">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp}>
              <Typography
                variant="h4"
                align="center"
                sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.5rem', md: '2rem' } }}
              >
                An Irish Astrology Business
              </Typography>
              <Typography variant="body1" align="center" sx={{ color: '#8a8a9a', mb: 5 }}>
                Real calculations. Real guidance. Real person.
              </Typography>
            </motion.div>

            <Grid container spacing={3} sx={{ mb: 5 }}>
              {[
                { number: '10+', label: 'Years of practice' },
                { number: '5,000+', label: 'Readings delivered' },
                { number: '12', label: 'Sign forecasts yearly' },
              ].map((stat) => (
                <Grid item xs={4} key={stat.label}>
                  <motion.div variants={fadeInUp}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#c9a87c', fontSize: { xs: '1.5rem', md: '2rem' } }}>
                        {stat.number}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#8a8a9a', fontSize: { xs: '0.7rem', md: '0.85rem' } }}>
                        {stat.label}
                      </Typography>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            <motion.div variants={fadeInUp}>
              <Typography
                variant="h5"
                align="center"
                sx={{ fontWeight: 600, mb: 4, fontSize: { xs: '1.2rem', md: '1.5rem' } }}
              >
                How it works
              </Typography>
            </motion.div>

            <Grid container spacing={3}>
              {[
                { step: '1', title: 'Tell us your birth details', desc: 'Date, time, and place of birth for accurate calculations' },
                { step: '2', title: 'Get your reading', desc: 'Real astronomical data translated into guidance you understand' },
                { step: '3', title: 'Live your best life', desc: 'Use cosmic timing to make better decisions every day' },
              ].map((item) => (
                <Grid item xs={12} md={4} key={item.step}>
                  <motion.div variants={fadeInUp}>
                    <Box sx={{ textAlign: 'center', px: 2 }}>
                      <Box
                        sx={{
                          width: 48, height: 48, borderRadius: '50%',
                          background: 'rgba(201,168,124,0.12)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          mx: 'auto', mb: 2,
                        }}
                      >
                        <Typography variant="h6" sx={{ color: '#c9a87c', fontWeight: 700 }}>
                          {item.step}
                        </Typography>
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#8a8a9a' }}>
                        {item.desc}
                      </Typography>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* ============ SECTION 7: STICKY BOTTOM BAR (Mobile) ============ */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'fixed',
          bottom: 64, // above mobile nav
          left: 0,
          right: 0,
          zIndex: 1100,
          background: 'rgba(10,10,15,0.95)',
          backdropFilter: 'blur(8px)',
          borderTop: '1px solid rgba(201,168,124,0.15)',
          px: 2,
          py: 1.5,
          gap: 1,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Button
          variant="contained"
          size="small"
          startIcon={<AutoAwesomeIcon />}
          onClick={() => navigate('/free-birth-chart')}
          sx={{
            background: 'linear-gradient(135deg, #c9a87c, #a88a5e)',
            color: '#0a0a0f',
            fontWeight: 700,
            fontSize: '0.75rem',
            px: 2,
            py: 1,
            flex: 1,
            maxWidth: 140,
            '&:hover': { background: 'linear-gradient(135deg, #dbc4a0, #c9a87c)' },
          }}
        >
          Start Free
        </Button>
      </Box>
    </Box>
  );
};

export default HomePage;
